// @flow
import immutable, { Record, Map } from "immutable"
import moment from "moment"
import type Moment from "moment"

import { showDate, isLocalOverNight } from "../../../app/scripts/containers/helpers/partHelper"
import {
  RESERVATION_PART_CONFIG,
  US_CUBA_REGULATION_COUNTRIES,
} from "../../booking/scenes/ReservationForm/consts/reservationConsts"
import type { FlightId } from "../../mmb/types/primitiveTypes"

export const BOARDING_PASS_AVAILABILITY_NEVER = "never"

export const getDuration = (aUtc: Moment, bUtc: Moment) => moment.duration(aUtc.diff(bUtc))

export function departureArrivalFromJS(da: any) {
  return Map({
    where: immutable.fromJS(da.where),
    when: Map({
      local: moment.utc(da.when.local),
      utc: moment.utc(da.when.utc),
    }),
  })
}

const FlightRecord = Record({
  // Common with Transfer
  id: null,
  naturalId: null,
  departure: null,
  updatedDeparture: null,
  arrival: null,
  updatedArrival: null,
  vehicle: null,
  type: RESERVATION_PART_CONFIG.flight.type,

  airline: null, // called carrier in Transfer
  seatmap: null,

  // Special for Flight
  number: null,
  bagsRecheckRequired: null, // bool
  guarantee: null, // bool
  isHidden: false, // bool

  boardingPassAvailableAt: null, // ?timestamp

  priorityBoarding: false,
  isCabinBagsWithPriorityBoarding: false,
  operatingAirline: null,
  pnr: null, // string

  status: null, // for schedule change

  amenities: null,

  fareCategory: null,
})

export default class Flight extends FlightRecord {
  id: FlightId

  constructor(data: any) {
    const naturalId = [
      data.number,
      data.airline && data.airline.get("code"),
      data.departure && data.departure.getIn(["where", "code"]),
      data.departure && data.departure.getIn(["when", "utc"], moment()).valueOf(),
      data.arrival && data.arrival.getIn(["where", "code"]),
      data.arrival && data.arrival.getIn(["when", "utc"], moment()).valueOf(),
    ].join(":")

    super({
      ...data,
      id: data.id || naturalId,
      naturalId,
    })
  }

  /**
   * @deprecated - use native getters instead
   */
  getId() {
    return this.get("id")
  }

  /**
   * @deprecated - use native getters instead
   */
  getDeparture() {
    return this.get("departure")
  }

  /**
   * @deprecated - use native getters instead
   */
  getArrival() {
    return this.get("arrival")
  }

  getDuration() {
    return getDuration(
      this.get("arrival")
        .get("when")
        .get("utc"),
      this.get("departure")
        .get("when")
        .get("utc"),
    )
  }

  hasDeparted(): boolean {
    const now = moment().utc()

    return now.isAfter(this.getIn(["departure", "when", "utc"]))
  }

  hasArrived(): boolean {
    const now = moment().utc()

    return now.isAfter(this.getIn(["arrival", "when", "utc"]))
  }

  getFrom() {
    return this.getIn(["departure", "where", "name"])
  }

  getTo() {
    return this.getIn(["arrival", "where", "name"])
  }

  isLocalOverNight(): boolean {
    return isLocalOverNight(this)
  }

  showDate(prevPart: Flight): boolean {
    return showDate(prevPart, this)
  }

  // noinspection JSMethodCanBeStatic
  /**
   * Just for mutual API with Journey/Trip
   */
  getStopsCount() {
    return 0
  }

  // noinspection JSMethodCanBeStatic
  /**
   * Just for mutual API with Journey/Trip
   */
  getFlightsCount() {
    return 1
  }

  getFlightNumber(delimiter: string = " "): ?string {
    // mmb api does not return flight_no - unable to generate flight number
    return this.get("number")
      ? [this.getIn(["airline", "code"]), delimiter, this.get("number")].join("")
      : null
  }

  getHoursToDeparture() {
    return this.getIn(["departure", "when", "utc"]).diff(moment.utc(), "hours")
  }

  getClosingHours() {
    return this.getIn(["airline", "closeBookingHours"])
  }

  hasDifferentOperatingAirline() {
    return (
      this.get("operatingAirline") &&
      this.getIn(["operatingAirline", "name"]) !== this.getIn(["airline", "name"])
    )
  }

  hasDelay() {
    return this.getIn(["status", "type"]) === "delayed"
  }

  getChangedDepartureWhere() {
    return this.getIn(["status", "changedDepartureWhere"])
  }

  getChangedArrivalWhere() {
    return this.getIn(["status", "changedArrivalWhere"])
  }

  getDelayedTimes() {
    return this.hasDelay() && this.getIn(["status", "changedTime"])
  }

  isFlightWithinCountry(country: string) {
    return [
      this.getIn(["departure", "where", "country"]),
      this.getIn(["arrival", "where", "country"]),
    ].every(x => x === country)
  }

  // Check if this flight is affected by `Cuba <=> US territories` regulations
  isCubaRegulationAffected() {
    const departureCountry = this.get("departure").getIn(["where", "country"])
    const arrivalCountry = this.get("arrival").getIn(["where", "country"])

    return (
      (arrivalCountry === "CU" && US_CUBA_REGULATION_COUNTRIES.includes(departureCountry)) ||
      (departureCountry === "CU" && US_CUBA_REGULATION_COUNTRIES.includes(arrivalCountry))
    )
  }

  static fromPlainJS(input: any) {
    return new Flight({
      ...input,
      departure: departureArrivalFromJS(input.departure),
      arrival: departureArrivalFromJS(input.arrival),
      vehicle: input.vehicle && immutable.fromJS(input.vehicle),
      airline: immutable.fromJS(input.airline),
      operatingAirline: input.operatingAirline && immutable.fromJS(input.operatingAirline),
    })
  }
}
